// Copyright 2026 TokenLat. Licensed under the Apache License, Version 2.0.
import { useRef, useState } from 'react';

import { Button, Modal } from '@coze-arch/coze-design';

import { AccountNotice, t } from './tokenlat-ui';
import { GroupSelector, ModelSelector } from './tokenlat-selectors';
import {
  AccountError,
  accountRequest,
  creditUnits,
  legacyQuota,
  quotaCurrency,
  type AccountOptions,
} from './tokenlat-api';

import styles from './tokenlat-account.module.less';
interface NewKeyProps {
  options: AccountOptions;
  onClose: () => void;
  onCreated: () => void;
}
function useNewKey({ options, onClose, onCreated }: NewKeyProps) {
  const [name, setName] = useState('');
  const [group, setGroup] = useState(
    options.default_use_auto_group && 'auto' in options.groups
      ? 'auto'
      : 'default' in options.groups
        ? 'default'
        : (Object.keys(options.groups)[0] ?? ''),
  );
  const [count, setCount] = useState(1);
  const [expiry, setExpiry] = useState('');
  const [unlimited, setUnlimited] = useState(true);
  const [amount, setAmount] = useState('');
  const [models, setModels] = useState<string[]>([]);
  const [allowIPs, setAllowIPs] = useState('');
  const [crossGroup, setCrossGroup] = useState(
    options.default_use_auto_group && 'auto' in options.groups,
  );
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(0);
  const [error, setError] = useState<unknown>();
  const [unconfirmed, setUnconfirmed] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  const submitted = useRef(false);
  const availableModels = options.models;

  const create = async () => {
    if (submitted.current || !form.current?.reportValidity()) {
      return;
    }
    setError(undefined);
    let units = '0';
    let quota = 0;
    try {
      if (!unlimited) {
        if (options.billing_mode === 'unified') {
          units = creditUnits(amount, options.credit_scale);
        } else {
          quota = legacyQuota(amount, options);
        }
      }
      if (
        !name.trim() ||
        !group ||
        !Number.isInteger(count) ||
        count < 1 ||
        count > 20
      ) {
        throw new AccountError('invalid_authorization');
      }
      const expiresAt = expiry
        ? Math.floor(new Date(expiry).getTime() / 1000)
        : -1;
      if (
        expiry &&
        (!Number.isFinite(expiresAt) || expiresAt <= Date.now() / 1000)
      ) {
        throw new AccountError('invalid_expiry');
      }
      submitted.current = true;
      setBusy(true);
      for (let index = 0; index < count; index++) {
        await accountRequest('/keys', {
          method: 'POST',
          body: {
            operation_id: crypto.randomUUID(),
            name: count === 1 ? name.trim() : `${name.trim()}-${index + 1}`,
            expired_time: expiresAt,
            group,
            unlimited_quota: unlimited,
            remain_quota: quota,
            credit_limit_units: units,
            model_limits_enabled: models.length > 0,
            model_limits: models.join(','),
            allow_ips: allowIPs.trim(),
            cross_group_retry: group === 'auto' && crossGroup,
          },
        });
        setCreated(index + 1);
      }
      onCreated();
      onClose();
    } catch (e) {
      setError(e);
      // A partially completed batch must not be submitted again as a new batch.
      setUnconfirmed(submitted.current);
      onCreated();
    } finally {
      setBusy(false);
    }
  };

  return {
    name,
    setName,
    group,
    setGroup,
    count,
    setCount,
    expiry,
    setExpiry,
    unlimited,
    setUnlimited,
    amount,
    setAmount,
    models,
    setModels,
    allowIPs,
    setAllowIPs,
    crossGroup,
    setCrossGroup,
    busy,
    created,
    error,
    unconfirmed,
    form,
    availableModels,
    create,
  };
}
function KeyRestrictions({ state }: { state: ReturnType<typeof useNewKey> }) {
  const disabled = state.busy || state.unconfirmed;
  return (
    <details className={styles.advanced}>
      <summary>
        {t('advanced_settings')}
        <span className={styles.muted}>{t('advanced_hint')}</span>
      </summary>
      <div className={styles['section-fields']}>
        <ModelSelector
          available={state.availableModels}
          selected={state.models}
          disabled={disabled}
          onChange={state.setModels}
        />
        <label>
          {t('ips')}
          <textarea
            value={state.allowIPs}
            disabled={disabled}
            rows={3}
            onChange={event => state.setAllowIPs(event.target.value)}
            placeholder={t('ips_placeholder')}
          />
          <span className={styles.muted}>{t('ips_hint')}</span>
        </label>
      </div>
    </details>
  );
}

function BasicKeyFields({
  state,
  options,
}: {
  state: ReturnType<typeof useNewKey>;
  options: AccountOptions;
}) {
  const disabled = state.busy || state.unconfirmed;
  const setExpiryPreset = (kind: 'never' | 'month' | 'day' | 'hour') => {
    if (kind === 'never') {
      state.setExpiry('');
      return;
    }
    const date = new Date();
    if (kind === 'month') {
      date.setMonth(date.getMonth() + 1);
    }
    if (kind === 'day') {
      date.setDate(date.getDate() + 1);
    }
    if (kind === 'hour') {
      date.setHours(date.getHours() + 1);
    }
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    state.setExpiry(local.toISOString().slice(0, 16));
  };
  return (
    <section className={styles['form-section']}>
      <h3>{t('basic_info')}</h3>
      <p className={styles.muted}>{t('basic_hint')}</p>
      <div className={styles['form-grid']}>
        <label>
          {t('name')}
          <input
            autoFocus
            value={state.name}
            placeholder={t('name_placeholder')}
            maxLength={state.count > 1 ? 47 : 50}
            required
            disabled={disabled}
            onChange={event => state.setName(event.target.value)}
          />
        </label>
        <label>
          {t('count')}
          <input
            type="number"
            min={1}
            max={20}
            step={1}
            required
            value={state.count}
            disabled={disabled}
            onChange={event => state.setCount(Number(event.target.value))}
          />
        </label>
      </div>
      <span className={styles.muted}>{t('batch_name_hint')}</span>
      <GroupSelector
        options={options}
        value={state.group}
        disabled={disabled}
        onChange={value => {
          state.setGroup(value);
          if (value !== 'auto') {
            state.setCrossGroup(false);
          }
        }}
      />
      {state.group === 'auto' ? (
        <label className={styles['switch-row']}>
          <span>
            {t('cross_group')}
            <span className={styles.muted}>{t('cross_group_hint')}</span>
          </span>
          <input
            type="checkbox"
            checked={state.crossGroup}
            disabled={disabled}
            onChange={event => state.setCrossGroup(event.target.checked)}
          />
        </label>
      ) : null}
      <label>
        {t('expiry')}
        <input
          type="datetime-local"
          aria-label={t('expiry')}
          value={state.expiry}
          disabled={disabled}
          onChange={event => state.setExpiry(event.target.value)}
        />
      </label>
      <div className={styles['expiry-presets']}>
        {(['never', 'month', 'day', 'hour'] as const).map(kind => (
          <button
            type="button"
            key={kind}
            disabled={disabled}
            className={styles.preset}
            onClick={() => setExpiryPreset(kind)}
          >
            {t(kind === 'never' ? 'never' : `one_${kind}`)}
          </button>
        ))}
      </div>
      {!state.expiry ? (
        <span className={styles.muted}>{t('never')}</span>
      ) : null}
    </section>
  );
}

export function NewKey(props: NewKeyProps) {
  const { options, onClose } = props;
  const state = useNewKey(props);
  const disabled = state.busy || state.unconfirmed;
  return (
    <Modal
      title={t('create')}
      visible
      width={680}
      style={{ maxWidth: 'calc(100vw - 24px)' }}
      onCancel={state.busy ? undefined : onClose}
      closable={!state.busy}
      maskClosable={false}
      footer={
        <div className={styles['create-footer']}>
          <span className={styles.muted}>{t('create_hint')}</span>
          <div className={styles.toolbar}>
            <Button disabled={state.busy} onClick={onClose}>
              {t('close')}
            </Button>
            <Button
              disabled={disabled}
              loading={state.busy}
              onClick={() => {
                void state.create();
              }}
            >
              {t('create')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        ref={state.form}
        className={styles.form}
        onSubmit={event => {
          event.preventDefault();
          void state.create();
        }}
      >
        <BasicKeyFields state={state} options={options} />
        <section className={styles['form-section']}>
          <h3>{t('quota_settings')}</h3>
          <label className={styles['switch-row']}>
            <span>
              {t('unlimited')}
              <span className={styles.muted}>{t('quota_hint')}</span>
            </span>
            <input
              type="checkbox"
              checked={state.unlimited}
              disabled={disabled}
              onChange={event => state.setUnlimited(event.target.checked)}
            />
          </label>
          {!state.unlimited ? (
            <label>
              {options.billing_mode === 'unified'
                ? t('credit_limit')
                : `${t('quota_amount')} (${quotaCurrency(options)})`}
              <input
                value={state.amount}
                required
                inputMode="decimal"
                disabled={disabled}
                onChange={event => state.setAmount(event.target.value)}
              />
            </label>
          ) : null}
        </section>
        <KeyRestrictions state={state} />
        <AccountNotice error={state.error} />
        {state.busy || state.unconfirmed ? (
          <p role="status">
            {t('created_count')}: {state.created} / {state.count}
          </p>
        ) : null}
        {state.unconfirmed ? <p>{t('batch_stopped')}</p> : null}
      </form>
    </Modal>
  );
}
